import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { UserPlus, Save, FileText, Upload, X, Plus } from 'lucide-react';
import { guestBooking, forms as formsApi } from '../utils/api';

/**
 * GuestBookingSettings — admin panel for roadmap item #20 Phase 1.
 *
 * Configures the public /book/guest page:
 *   - master enable toggle
 *   - which contact fields are required
 *   - whether pet info must be collected
 *
 * Phase 2 will add document/form gates and a "promote guest to full
 * client" flow; not present here.
 */

const FIELD_OPTIONS = [
  { key: 'email',     label: 'Email address' },
  { key: 'phone',     label: 'Phone number' },
  { key: 'address',   label: 'Address' },
  { key: 'pet_name',  label: 'Pet name' },
  { key: 'pet_breed', label: 'Pet breed' },
  { key: 'pet_size',  label: 'Pet size' },
  { key: 'notes',     label: 'Notes / special requests' },
];

const GuestBookingSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [petRequired, setPetRequired] = useState(true);
  const [requiredFields, setRequiredFields] = useState(['email', 'phone']);
  // Phase 2c: form IDs the guest must complete before booking.
  const [requiredFormIds, setRequiredFormIds] = useState([]);
  const [allForms, setAllForms] = useState([]);
  // Phase 2c (docs): free-text labels for files the guest must upload
  // before booking. Stored as a string array; the editor below adds
  // an empty trailing slot so admins can keep typing.
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      // /config returns the resolved required_forms (post-filter for
      // active+existing), but we also need the full active form list
      // so the admin can add new ones.
      const [cfg, formsRes] = await Promise.all([
        guestBooking.config(),
        formsApi.list().catch(() => ({ data: [] })),
      ]);
      setEnabled(!!cfg.data.enabled);
      setPetRequired(!!cfg.data.pet_required);
      setRequiredFields(Array.isArray(cfg.data.required_fields) ? cfg.data.required_fields : []);
      // Prefer the resolved IDs (server-filtered) so a deleted form
      // doesn't haunt the UI as a phantom checked row.
      const resolvedIds = Array.isArray(cfg.data.required_forms)
        ? cfg.data.required_forms.map((f) => f.id)
        : [];
      setRequiredFormIds(resolvedIds);
      const activeForms = (Array.isArray(formsRes.data) ? formsRes.data : []).filter(
        (f) => f.is_active === undefined || f.is_active === true || f.is_active === 1
      );
      setAllForms(activeForms);
      setRequiredDocuments(Array.isArray(cfg.data.required_documents) ? cfg.data.required_documents : []);
    } catch (e) {
      console.error('Load guest settings:', e);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (key) => {
    setRequiredFields((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await guestBooking.updateSettings({
        enabled,
        pet_required: petRequired,
        required_fields: requiredFields,
        required_form_ids: requiredFormIds,
        required_documents: requiredDocuments.map((s) => String(s || '').trim()).filter(Boolean),
      });
      toast.success('Guest booking settings saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <UserPlus className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Guest Booking</h2>
      </div>

      <p className="text-gray-600 mb-4 text-sm">
        Let prospects book one-shot appointments at <code className="px-1 bg-gray-100 rounded">/book/guest</code> without
        creating a full account. Each submission creates a guest record
        plus an appointment; admins can manage these from the
        appointments page.
      </p>

      <label className="flex items-center gap-3 mb-3 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span className="font-medium text-gray-800">Enable guest booking</span>
      </label>

      <label className={`flex items-center gap-3 mb-4 cursor-pointer ${!enabled ? 'opacity-50' : ''}`}>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={petRequired}
          disabled={!enabled}
          onChange={(e) => setPetRequired(e.target.checked)}
        />
        <span className="text-gray-800">Require pet information (turn off for non-pet services)</span>
      </label>

      <div className={!enabled ? 'opacity-50' : ''}>
        <div className="text-sm font-medium text-gray-700 mb-2">Required fields on the public form</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {FIELD_OPTIONS.map((opt) => (
            <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={requiredFields.includes(opt.key)}
                disabled={!enabled}
                onChange={() => toggleField(opt.key)}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={`border-t pt-4 mt-2 ${!enabled ? 'opacity-50' : ''}`}>
        <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
          <FileText size={14} />
          <span>Required intake forms</span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Guests must complete every checked form before their booking is
          accepted (Phase 2c). The list shows currently active forms.
        </p>
        {allForms.length === 0 ? (
          <div className="text-sm text-gray-500 italic">No active forms. Create one in the Forms manager first.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {allForms.map((f) => (
              <label key={f.id} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 mt-1"
                  checked={requiredFormIds.includes(f.id)}
                  disabled={!enabled}
                  onChange={() => setRequiredFormIds((prev) =>
                    prev.includes(f.id) ? prev.filter((x) => x !== f.id) : [...prev, f.id]
                  )}
                />
                <span className="text-sm">
                  <span className="font-medium">{f.name}</span>
                  {f.description ? <span className="block text-xs text-gray-500">{f.description}</span> : null}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={`border-t pt-4 mt-2 ${!enabled ? 'opacity-50' : ''}`}>
        <div className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
          <Upload size={14} />
          <span>Required document uploads</span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          One file upload slot per label (e.g. "Vaccination records",
          "Photo ID"). Guests must upload one file per label before
          their booking is accepted.
        </p>
        <div className="space-y-2 mb-2">
          {requiredDocuments.map((label, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                className="input flex-1"
                placeholder="e.g. Vaccination records"
                disabled={!enabled}
                value={label}
                onChange={(e) => setRequiredDocuments((prev) => prev.map((v, i) => i === idx ? e.target.value : v))}
              />
              <button
                type="button"
                onClick={() => setRequiredDocuments((prev) => prev.filter((_, i) => i !== idx))}
                disabled={!enabled}
                className="btn btn-secondary"
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setRequiredDocuments((prev) => [...prev, ''])}
          disabled={!enabled}
          className="btn btn-secondary flex items-center gap-2 text-sm"
        >
          <Plus size={14} /> Add document
        </button>
      </div>

      <button onClick={handleSave} disabled={saving} className="btn btn-primary flex items-center gap-2">
        <Save size={16} />
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  );
};

export default GuestBookingSettings;
